import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CurrencyContext = createContext(null);

const currencySymbols = {
  CLP: '$',
  USD: 'US$',
  EUR: '€'
};

const currencyFlags = {
  CLP: '🇨🇱',
  USD: '🇺🇸',
  EUR: '🇪🇺'
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within CurrencyProvider');
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('currency') || 'CLP';
  });
  const [rates, setRates] = useState({ CLP: 1, USD: 0.0011, EUR: 0.0010 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem('currency', currency);
  }, [currency]);

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/currency/rates?base=CLP`);
      setRates(response.data.rates);
    } catch (error) {
      console.error('Failed to fetch rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const convert = useCallback((amount, fromCurrency = 'CLP') => {
    if (currency === fromCurrency) return amount;
    
    // Convert to CLP first if not already
    let amountInCLP = amount;
    if (fromCurrency !== 'CLP') {
      amountInCLP = amount / rates[fromCurrency];
    }
    
    // Convert from CLP to target currency
    return amountInCLP * rates[currency];
  }, [currency, rates]);

  const formatPrice = useCallback((amount, fromCurrency = 'CLP') => {
    if (amount === 0 || amount === null || amount === undefined) return null;
    
    const converted = convert(amount, fromCurrency);
    const symbol = currencySymbols[currency];
    
    if (currency === 'CLP') {
      return `${symbol}${Math.round(converted).toLocaleString('es-CL')}`;
    }
    return `${symbol}${converted.toFixed(2)}`;
  }, [currency, convert]);

  const changeCurrency = (newCurrency) => {
    if (currencySymbols[newCurrency]) {
      setCurrency(newCurrency);
    }
  };

  return (
    <CurrencyContext.Provider value={{
      currency,
      changeCurrency,
      convert,
      formatPrice,
      rates,
      loading,
      currencies: Object.keys(currencySymbols),
      symbols: currencySymbols,
      flags: currencyFlags
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};
